import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CollapsibleMultiselect from '../../../Multiselects/CollapsibleMultiselect';
import typeOf from '../../../../helpers/typeOf';
import query from '../../../../helpers/query';
import config from '../../../../config';
import CustomModal from "../../../Modal";

const GoalsPageFilter = ({ 
    location, 
    locales, 
    access, 
    searchString, 
    accessGranted, 
    offerLocales, 
    statuses, 
    modalIsVisible, 
    onAction, 
    onClose, 
    t 
}) => {
    const parsedQuery = query.parse(location && location.query);

    const [state, setState] = useState({
        locales: locales || [],
        access: access || [],
        searchString: parsedQuery.searchString || searchString || '',
        accessGranted: parsedQuery.accessGranted || accessGranted || [],
        offerLocales: parsedQuery.offerLocales || offerLocales || [],
        statuses: statuses === null ? [] : statuses || [{value: 'active'}],
        modalIsVisible: modalIsVisible || false
    });

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            locales: locales || [],
            modalIsVisible: modalIsVisible,
            searchString: searchString || ''
        }));
    }, [locales, modalIsVisible, searchString]);

    useEffect(() => {
        if (onAction) {
            onAction('init');
        }
    }, []);

    const handleAction = useCallback((type, newValue) => {
        let stateUpdate = {
            searchString: state.searchString,
            accessGranted: state.accessGranted,
            offerLocales: state.offerLocales,
            statuses: state.statuses,
            page: config.initialPage
        };

        if(newValue) {
            for( let key in newValue) {
                stateUpdate[key] = newValue[key];
            }
        }

        type === 'init' && delete stateUpdate.page;

        if (onClose) {
            onClose();
        }

        if (onAction) {
            onAction(stateUpdate);
        }
    }, [state.searchString, state.accessGranted, state.offerLocales, state.statuses, onClose, onAction]);

    const handleSearchChange = useCallback((e) => {
        handleAction(null, {searchString: e.target.value});
        setState(prevState => ({
            ...prevState,
            searchString: e.target.value
        }));
    }, [handleAction]);

    const handleAccessSelectChange = useCallback((items) => {
        setState(prevState => ({
            ...prevState,
            accessGranted: items
        }));
    }, []);

    const handleLocalesSelectChange = useCallback((items) => {
        setState(prevState => ({
            ...prevState,
            offerLocales: items
        }));
    }, []);

    const handleStatusesSelectChange = useCallback((items) => {
        setState(prevState => ({
            ...prevState,
            statuses: items
        }));
    }, []);

    const getNumberFilledFields = useCallback(() => {
        const nameKeys = ['searchString', 'accessGranted', 'offerLocales', 'statuses'];
        let count = 0;

        nameKeys.map((name) => {
            let value = state[name];

            if (value) {
                if (
                    typeOf.isArray(value) && value.length
                    || typeOf.isString(value)
                ) {
                    count += 1;
                }
            }
        });

        return count;
    }, [state]);

    const staticNames = t?.offersPage?.goalsList || {};

    return (
        <div className="filter-construrtor">
            <CustomModal
                className="filter-modal"
                visible={state.modalIsVisible}
                title={staticNames.filter}
                areNoControls={false}
                onConfirm={handleAction}
                onClose={onClose}
            >
                <div>
                    <div className="hidden">
                        <label className="input-label bold">{staticNames.byGoalName} <i className="fa fa-search"></i> </label>
                        <input
                            className="form-control"
                            type="text"
                            placeholder={staticNames.goalName}
                            pattern=".{3,}"
                            title={staticNames.minSymbols}
                            onChange={handleSearchChange}
                            value={state.searchString}
                        />
                    </div>
                    <div className="form-group">
                        <label className="control-label">{staticNames.byAccess}</label>
                        <CollapsibleMultiselect
                            className={'select-with-chevron'}
                            messages={{emptyList: staticNames.emptyList}}
                            dataKey="value"
                            textField='title'
                            onChange={handleAccessSelectChange}
                            data={state.access}
                            value={state.accessGranted}
                        />
                    </div>
                    <div className="form-group">
                        <label className="control-label">{staticNames.byLocales}</label>
                        <CollapsibleMultiselect
                            className={'select-with-chevron'}
                            messages={{emptyList: staticNames.emptyList}}
                            dataKey="value"
                            textField='title'
                            onChange={handleLocalesSelectChange}
                            data={state.locales}
                            value={state.offerLocales}
                        />
                    </div>
                    <div className="form-group">
                        <label className="control-label">{staticNames.byStatus}</label>
                        <CollapsibleMultiselect
                            className={'select-with-chevron'}
                            messages={{emptyList: staticNames.emptyList}}
                            dataKey="value"
                            textField='title'
                            onChange={handleStatusesSelectChange}
                            data={statuses}
                            value={state.statuses}
                        />
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};

GoalsPageFilter.propTypes = {
    location: PropTypes.object,
    locales: PropTypes.array,
    access: PropTypes.array,
    searchString: PropTypes.string,
    accessGranted: PropTypes.array,
    offerLocales: PropTypes.array,
    statuses: PropTypes.array,
    modalIsVisible: PropTypes.bool,
    onAction: PropTypes.func,
    onClose: PropTypes.func,
    t: PropTypes.object
};

GoalsPageFilter.defaultProps = {
    locales: [],
    access: [],
    searchString: '',
    accessGranted: [],
    offerLocales: [],
    statuses: [],
    modalIsVisible: false,
    onAction: () => {},
    onClose: () => {},
    t: {}
};

export default withTranslation(GoalsPageFilter);